import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import {
  UserIcon,
  MailIcon,
  PhoneIcon,
  ShieldCheckIcon,
  PencilIcon,
  CameraIcon,
  SaveIcon,
} from "@heroicons/react/outline";
import toast from "react-hot-toast";

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });

  const handleSave = () => {
    updateUser(formData);
    setIsEditing(false);
    toast.success("Profile updated successfully!");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Profile
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Manage your personal information
          </p>
        </div>
        <button
          onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
          className="btn-primary flex items-center gap-2"
        >
          {isEditing ? (
            <>
              <SaveIcon className="h-5 w-5" />
              Save Changes
            </>
          ) : (
            <>
              <PencilIcon className="h-5 w-5" />
              Edit Profile
            </>
          )}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="card text-center">
            <div className="relative inline-block">
              <div className="w-32 h-32 bg-gradient-to-br from-primary-500 to-primary-700 rounded-full mx-auto flex items-center justify-center text-white text-4xl font-bold">
                {user?.full_name
                  ?.split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase() || "U"}
              </div>
              <button className="absolute bottom-0 right-0 p-2 bg-primary-500 rounded-full text-white hover:bg-primary-600 transition-colors">
                <CameraIcon className="h-5 w-5" />
              </button>
            </div>
            <h2 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">
              {user?.full_name || "User"}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {user?.role?.replace("_", " ").toUpperCase()}
            </p>
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <ShieldCheckIcon className="h-4 w-4" />
                <span>{user?.email}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-2">
          <div className="card">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
              Personal Information
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  <UserIcon className="h-4 w-4 inline mr-2" />
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  disabled={!isEditing}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isEditing
                      ? "border-primary-500 focus:ring-2 focus:ring-primary-500"
                      : "border-slate-200 dark:border-slate-600"
                  } bg-white dark:bg-slate-700 text-slate-900 dark:text-white transition-colors`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  <MailIcon className="h-4 w-4 inline mr-2" />
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  disabled={!isEditing}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isEditing
                      ? "border-primary-500 focus:ring-2 focus:ring-primary-500"
                      : "border-slate-200 dark:border-slate-600"
                  } bg-white dark:bg-slate-700 text-slate-900 dark:text-white transition-colors`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  <PhoneIcon className="h-4 w-4 inline mr-2" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phone || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  disabled={!isEditing}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isEditing
                      ? "border-primary-500 focus:ring-2 focus:ring-primary-500"
                      : "border-slate-200 dark:border-slate-600"
                  } bg-white dark:bg-slate-700 text-slate-900 dark:text-white transition-colors`}
                  placeholder="+251-911-000000"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
